/**
 * 오탐지 피드백 시 필드별 값 입력 가이드
 * 사용자가 오탐지를 신고할 때 각 필드에 넣어야 할 표준화된 값들
 */

export interface FalsePositiveFeedbackGuide {
  actualResult: 'FALSE_POSITIVE';
  userFeedback: string;
  misclassificationCause: string;
  improvementScore: number;
  correctionTime: Date;
  isLearningApplied: boolean;
}

/**
 * 오탐지 원인별 표준화된 카테고리
 */
export const FALSE_NEGATIVE_CAUSES = {
  // 1. 화재 크기/규모 - 필수
  FIRE_SIZE: {
    required: true,
    label: '화재 크기/규모',
    INITIAL_FIRE: {
      code: '초기화재',
      description: '매우 작은 불꽃, 발화 단계',
      examples: ['작은 전선 발화', '초기 발화 단계', '미세한 불꽃'],
    },
    SMALL_FIRE: {
      code: '소규모화재',
      description: '작은 범위의 화재',
      examples: ['국소적 화재', '작은 범위 연소', '제한적 확산'],
    },
    MEDIUM_FIRE: {
      code: '중규모화재',
      description: '중간 크기의 화재',
      examples: ['중간 규모 확산', '여러 구역 연소', '상당한 연기 발생'],
    },
    LARGE_FIRE: {
      code: '대규모화재',
      description: '큰 범위의 화재',
      examples: ['광범위 확산', '대량 연기 발생', '구조물 위험'],
    },
  },

  // 2. 화재 종류 - 필수
  FIRE_TYPE: {
    required: true,
    label: '화재 종류',
    ELECTRICAL: {
      code: '전기화재',
      description: '전선, 배전반, 전기설비',
      examples: ['전선 합선', '배전반 화재', '전기 장비 과열'],
    },
    COMBUSTIBLE: {
      code: '가연물화재',
      description: '목재, 종이, 플라스틱 등',
      examples: ['목재 연소', '종이류 화재', '플라스틱 연소'],
    },
    LIQUID_FUEL: {
      code: '유류화재',
      description: '기름, 연료 등 액체 가연물',
      examples: ['휘발유 화재', '경유 연소', '윤활유 화재'],
    },
    GAS: {
      code: '가스화재',
      description: 'LPG, 도시가스 등',
      examples: ['LPG 누출 화재', '도시가스 연소', '프로판 가스'],
    },
    OTHER: {
      code: '기타화재',
      description: '위 분류에 속하지 않는 화재',
      examples: ['화학물질 화재', '복합 화재', '미분류 화재'],
    },
  },

  // 3. 감지 실패 주요 원인 - 필수
  DETECTION_FAILURE_CAUSE: {
    required: true,
    label: '감지 실패 주요 원인',
    SMALL_SCALE: {
      code: '화재규모작음',
      description: '불꽃이나 연기가 너무 작아서',
      examples: ['미세한 불꽃', '소량의 연기', '초기 단계'],
    },
    LIGHTING_INTERFERENCE: {
      code: '주변조명방해',
      description: '작업등, 햇빛 등으로 구분 어려움',
      examples: ['강한 작업등', '햇빛 반사', '조명 간섭'],
    },
    INSUFFICIENT_SMOKE: {
      code: '연기부족',
      description: '연기가 거의 없어서',
      examples: ['무연 연소', '전기 화재', '알코올 연소'],
    },
    CAMERA_ANGLE: {
      code: '카메라각도문제',
      description: '사각지대나 각도 문제',
      examples: ['시야 차단', '각도 부적절', '거리 문제'],
    },
    WEATHER_EFFECT: {
      code: '날씨영향',
      description: '비, 안개, 바람 등',
      examples: ['안개로 시야 방해', '비로 인한 화질 저하', '바람으로 연기 분산'],
    },
    VIDEO_QUALITY: {
      code: '화면품질문제',
      description: '해상도, 밝기 등 화질 문제',
      examples: ['낮은 해상도', '어두운 화면', '블러 현상'],
    },
  },
};
export const FALSE_POSITIVE_CAUSES = {
  LIGHTING: {
    code: '조명 반사',
    description: '인공 조명에 의한 강한 반사',
    examples: ['LED 조명', '형광등', '작업등'],
  },
  SUNLIGHT: {
    code: '햇빛 반사',
    description: '자연광(햇빛)에 의한 반사',
    examples: ['금속 표면 반사', '유리창 반사', '물 표면 반사'],
  },
  STEAM: {
    code: '무해한 증기',
    description: '화재가 아닌 일반적인 증기나 수증기',
    examples: ['온수 증기', '요리 증기', '샤워 증기'],
  },
  DUST: {
    code: '먼지/분진',
    description: '작업 중 발생하는 먼지나 분진',
    examples: ['시멘트 분진', '절단 작업 분진', '청소 중 먼지'],
  },
  SHADOW: {
    code: '그림자/음영',
    description: '조명이나 물체에 의한 그림자 효과',
    examples: ['구조물 그림자', '사람 그림자', '장비 그림자'],
  },
  WELDING: {
    code: '용접 작업',
    description: '정상적인 용접 작업으로 인한 불꽃',
    examples: ['전기 용접', '가스 용접', '절단 작업'],
  },
  ELECTRICAL: {
    code: '전기 장비',
    description: '전기 장비의 정상 작동으로 인한 빛',
    examples: ['모터 스파크', '전기 접점', '인디케이터 LED'],
  },
  VEHICLE: {
    code: '차량 관련',
    description: '차량의 라이트나 배기가스',
    examples: ['헤드라이트', '브레이크등', '배기가스'],
  },
  WEATHER: {
    code: '날씨 현상',
    description: '기상 조건으로 인한 현상',
    examples: ['안개', '비', '눈', '구름'],
  },
  OTHER: {
    code: '기타',
    description: '위 카테고리에 해당하지 않는 기타 원인',
    examples: ['미분류 원인'],
  },
} as const;

/**
 * 개선 점수 산정 가이드
 */
export const IMPROVEMENT_SCORE_GUIDE = {
  HIGH_VALUE: {
    score: 85 - 95,
    description: '매우 학습 가치가 높은 케이스',
    criteria: ['명확한 오분류 원인이 식별됨', '유사한 케이스가 자주 발생할 가능성', '패턴 학습으로 개선 가능성이 높음'],
  },
  MEDIUM_VALUE: {
    score: 70 - 84,
    description: '중간 정도의 학습 가치',
    criteria: ['원인이 어느 정도 명확함', '간헐적으로 발생할 수 있는 케이스', '조건부 개선 가능'],
  },
  LOW_VALUE: {
    score: 50 - 69,
    description: '낮은 학습 가치',
    criteria: ['원인이 불분명하거나 일회성', '예외적인 상황', '학습으로 개선하기 어려운 케이스'],
  },
  MINIMAL_VALUE: {
    score: 0 - 49,
    description: '최소한의 학습 가치',
    criteria: ['매우 특수한 상황', '재현 가능성이 낮음', '시스템 개선에 기여도가 낮음'],
  },
} as const;

/**
 * 오탐지 피드백 예시들
 */
export const FALSE_POSITIVE_EXAMPLES: FalsePositiveFeedbackGuide[] = [
  {
    actualResult: 'FALSE_POSITIVE',
    userFeedback: '작업장 상단의 강한 LED 조명이 금속 파이프에 반사되어 화재로 오인된 것으로 보입니다. 실제로는 화재나 연기가 전혀 없었습니다.',
    misclassificationCause: '조명 반사',
    improvementScore: 88,
    correctionTime: new Date(),
    isLearningApplied: false,
  },
  {
    actualResult: 'FALSE_POSITIVE',
    userFeedback: '오후 3시경 서쪽 창문으로 들어오는 강한 햇빛이 스테인리스 장비에 반사되어 화재 알람이 발생했습니다. 현장 확인 결과 화재는 없었습니다.',
    misclassificationCause: '햇빛 반사',
    improvementScore: 92,
    correctionTime: new Date(),
    isLearningApplied: false,
  },
  {
    actualResult: 'FALSE_POSITIVE',
    userFeedback: '온수 보일러에서 나오는 정상적인 수증기를 연기로 오인한 것 같습니다. 보일러는 정상 작동 중이었고 화재 위험은 없었습니다.',
    misclassificationCause: '무해한 증기',
    improvementScore: 85,
    correctionTime: new Date(),
    isLearningApplied: false,
  },
  {
    actualResult: 'FALSE_POSITIVE',
    userFeedback: '콘크리트 절단 작업 중 발생한 분진을 연기로 잘못 감지했습니다. 작업자들이 정상적인 안전 장비를 착용하고 작업 중이었습니다.',
    misclassificationCause: '먼지/분진',
    improvementScore: 90,
    correctionTime: new Date(),
    isLearningApplied: false,
  },
  {
    actualResult: 'FALSE_POSITIVE',
    userFeedback: '정상적인 용접 작업 중 발생한 불꽃과 연기를 화재로 오인했습니다. 작업자가 안전 수칙을 준수하며 작업 중이었습니다.',
    misclassificationCause: '용접 작업',
    improvementScore: 87,
    correctionTime: new Date(),
    isLearningApplied: false,
  },
];

/**
 * 사용자 피드백 작성 가이드
 */
export const USER_FEEDBACK_WRITING_GUIDE = {
  STRUCTURE: {
    situation: '상황 설명 (무엇이 일어났는지)',
    cause: '오탐지 원인 (왜 잘못 감지되었는지)',
    confirmation: '현장 확인 결과 (실제 상황)',
  },
  GOOD_EXAMPLES: [
    '강한 LED 조명이 금속 표면에 반사되어 화재로 오인되었습니다. 현장 확인 결과 화재는 없었습니다.',
    '정상적인 용접 작업 중 발생한 불꽃을 화재로 감지했습니다. 작업자가 안전 수칙을 준수하며 작업 중이었습니다.',
    '보일러에서 나오는 정상적인 수증기를 연기로 오인했습니다. 설비는 정상 작동 중이었습니다.',
  ],
  AVOID: [
    '오탐지입니다', // 너무 간단
    '틀렸어요', // 정보 부족
    '잘못된 감지', // 원인 미제공
  ],
  RECOMMENDED_LENGTH: {
    min: 30,
    max: 500,
    optimal: 100 - 200,
  },
} as const;

/**
 * 피드백 품질 평가 함수
 */
export function evaluateFeedbackQuality(feedback: string): {
  score: number;
  suggestions: string[];
} {
  const suggestions: string[] = [];
  let score = 100;

  // 길이 검사
  if (feedback.length < 30) {
    score -= 30;
    suggestions.push('더 구체적인 설명을 추가해주세요');
  }

  if (feedback.length > 500) {
    score -= 10;
    suggestions.push('간결하게 요약해주세요');
  }

  // 키워드 검사
  const situationKeywords = ['반사', '조명', '햇빛', '증기', '먼지', '용접', '작업'];
  const confirmationKeywords = ['확인', '없었음', '정상', '안전'];

  const hasSituation = situationKeywords.some((keyword) => feedback.includes(keyword));
  const hasConfirmation = confirmationKeywords.some((keyword) => feedback.includes(keyword));

  if (!hasSituation) {
    score -= 20;
    suggestions.push('오탐지 원인을 구체적으로 설명해주세요');
  }

  if (!hasConfirmation) {
    score -= 15;
    suggestions.push('현장 확인 결과를 포함해주세요');
  }

  return { score: Math.max(0, score), suggestions };
}
